/**
 * Cómo se ordena la pestaña «Documentos» del panel de la conversación.
 *
 * POR QUÉ EXISTE (DEF-017, 01-oct-2026)
 * -------------------------------------
 * Tras reiniciar cuatro escenarios, la pestaña ofrecía «elegir uno de los 21
 * que ya están en el chat»: los documentos de los expedientes descartados, los
 * rechazados al recibirlos y los del caso en curso, todos como tarjetas
 * iguales. Hoy son dos listas —Vigentes y Anteriores— y los rechazados por la
 * guarda de ingesta van juntos en un bloque compacto, no como tarjetas.
 *
 * Funciones puras y sin imports a propósito: el test las transpila con el
 * TypeScript del portal y las corre en Node
 * (`tests/unit/test_documentos_vigentes_y_anteriores.py`).
 */

/** Perú no tiene horario de verano: UTC-5 todo el año. El día de un documento
 *  es el de Lima, no el de UTC — un DNI de las 22:00 es de ese día, no del
 *  siguiente. Es la misma regla que `observabilidad/jornada.py`. */
const OFFSET_LIMA_MS = 5 * 3600 * 1000

/** `AAAA-MM-DD` del día en Lima, o '' si la fecha no se puede leer. */
export function diaEnLima(ts: string | null | undefined): string {
  const ms = Date.parse(ts || '')
  if (Number.isNaN(ms)) return ''
  return new Date(ms - OFFSET_LIMA_MS).toISOString().slice(0, 10)
}

/**
 * «05/10 16:35»: día, mes y hora de Lima, en 24 h. O '' si no se puede leer.
 *
 * Con la misma cuenta que `diaEnLima` y no con `toLocaleString('es-PE')`, que
 * da «5/10, 04:35 p. m.» y cambia con el ICU del navegador: es lo que el
 * asesor compara contra la hora del chat y tiene que leerse igual en todos
 * lados (DEF-051, DEF-056).
 */
export function fechaHoraEnLima(ts: string | null | undefined): string {
  const ms = Date.parse(ts || '')
  if (Number.isNaN(ms)) return ''
  const iso = new Date(ms - OFFSET_LIMA_MS).toISOString()
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`
}

/** Lo que `etiquetaDeVersion` lee de cada documento. */
export interface DocConVersion {
  doc_id: string
  doc_type: string
  created_at: string
  version_preferida?: string
}

/**
 * La marca de §9.15 en la tarjeta: cuál de las versiones se usa y cuándo llegó.
 *
 * DEF-056 (QA de TEST, 05-oct-2026): «Versión en uso · 1 de 2» se leía como
 * «la primera de dos», o sea la más vieja, sobre la que el sistema eligió.
 * Ahora dice lo que es y desde cuándo:
 *
 *   'si'  «Versión vigente · llegó 05/10 16:35»
 *   'no'  «Versión anterior · llegó 05/10 09:21» si llegó ANTES que la vigente
 *         de su tipo, y «Versión no usada · llegó …» si no: §9.15 elige la más
 *         legible, así que un reenvío más nuevo y peor también queda afuera, y
 *         llamarlo «anterior» sería falso.
 *   ''    nada: hubo una sola versión.
 */
export function etiquetaDeVersion(doc: DocConVersion,
                                  docs: ReadonlyArray<DocConVersion>): string {
  const llego = fechaHoraEnLima(doc.created_at)
  const cuando = llego ? ` · llegó ${llego}` : ''
  if (doc.version_preferida === 'si') return `Versión vigente${cuando}`
  if (doc.version_preferida !== 'no') return ''
  const propia = Date.parse(doc.created_at || '')
  const despues = !Number.isNaN(propia) && docs.some((d) => d.doc_id !== doc.doc_id
    && d.version_preferida === 'si' && d.doc_type === doc.doc_type
    && Date.parse(d.created_at || '') > propia)
  return `${despues ? 'Versión anterior' : 'Versión no usada'}${cuando}`
}

/** Cuántos documentos esperan la revisión de una persona: el badge de la
 *  pestaña «Documentos» y el del riel del cajón (DEF-031). Uno solo, para que
 *  los dos cuenten lo mismo. Un documento que el cliente descartó no está en la
 *  lista: los que llegan acá son los del expediente vigente. */
const POR_REVISAR = new Set(['NEEDS_REVIEW', 'AWAITING_REVIEW', 'READY'])

export function esPorRevisar(doc: { status: string }): boolean {
  return POR_REVISAR.has(doc.status)
}

export function cuantosPorRevisar(docs: ReadonlyArray<{ status: string }>): number {
  return docs.filter(esPorRevisar).length
}

/** Los rechazados por la guarda al recibirlos, aparte de los que se revisan.
 *  El backend lo decide por `reject_reason`, no por el `status`: lo que rechaza
 *  el revisor también queda en REJECTED y ése sí va con su tarjeta. */
export function separarRechazados<T extends { rechazado_en_ingesta?: boolean }>(
  docs: ReadonlyArray<T>,
): { tarjetas: T[]; rechazados: T[] } {
  const tarjetas: T[] = []
  const rechazados: T[] = []
  for (const d of docs) (d.rechazado_en_ingesta ? rechazados : tarjetas).push(d)
  return { tarjetas, rechazados }
}

export interface GrupoDelDia<T> {
  /** `AAAA-MM-DD` en Lima; '' para los que no traen fecha. */
  dia: string
  documentos: T[]
}

/** Agrupa por día de Lima: el día más reciente primero, y dentro de cada día
 *  del más nuevo al más viejo. Los que no traen fecha van en un grupo al
 *  final: no se pueden ubicar, pero tampoco se pueden perder. */
export function agruparPorDia<T extends { created_at: string }>(
  docs: ReadonlyArray<T>,
): GrupoDelDia<T>[] {
  const porDia = new Map<string, T[]>()
  for (const d of docs) {
    const dia = diaEnLima(d.created_at)
    const lista = porDia.get(dia) ?? []
    lista.push(d)
    porDia.set(dia, lista)
  }
  const dias = [...porDia.keys()].sort((a, b) => {
    if (!a) return 1
    if (!b) return -1
    return a < b ? 1 : a > b ? -1 : 0
  })
  return dias.map((dia) => ({
    dia,
    documentos: [...(porDia.get(dia) ?? [])].sort((a, b) =>
      (b.created_at || '').localeCompare(a.created_at || '')),
  }))
}

/**
 * «N intentos anteriores» de un requisito del checklist, o '' si no hubo.
 *
 * DEF-022 (QA, 01-oct-2026): al retomar con el agente los intentos de §9.27
 * vuelven a cero —si no, el cliente derivado por tres DNIs fallidos no podía
 * completar nunca ese requisito— y el historial queda en `intentos_previos`.
 * Nadie lo mostraba: el asesor que retoma no sabía que el cliente ya había
 * fallado ese documento.
 */
export function intentosAnteriores(n: number | null | undefined): string {
  const cuantos = Math.trunc(Number(n) || 0)
  if (cuantos <= 0) return ''
  return cuantos === 1 ? '1 intento anterior' : `${cuantos} intentos anteriores`
}

/** «miércoles 1 de octubre de 2026», o «Sin fecha». */
export function etiquetaDelDia(dia: string): string {
  if (!dia) return 'Sin fecha'
  // Al mediodía UTC para que ninguna zona horaria lo corra de día.
  return new Date(`${dia}T12:00:00Z`).toLocaleDateString('es-PE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  })
}
